import React from 'react'

export interface IconButtonProps {
  icon: React.ReactNode
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  variant?: 'default' | 'danger' | 'primary' | 'success'
  size?: 'sm' | 'md' | 'lg'
  title?: string
  disabled?: boolean
  className?: string
}

const variantStyles: Record<string, string> = {
  default: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
  danger: 'text-red-600 hover:text-red-900 hover:bg-red-50',
  primary: 'text-blue-600 hover:text-blue-900 hover:bg-blue-50',
  success: 'text-green-600 hover:text-green-900 hover:bg-green-50',
}

const sizeStyles: Record<string, string> = {
  sm: 'p-1',
  md: 'p-1.5',
  lg: 'p-2',
}

const iconSizeStyles: Record<string, string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
}

function IconButton({
  icon,
  onClick,
  variant = 'default',
  size = 'sm',
  title,
  disabled = false,
  className = '',
}: IconButtonProps) {
  const baseStyles = 'rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2'
  const variantStyle = variantStyles[variant] || variantStyles.default
  const sizeStyle = sizeStyles[size] || sizeStyles.sm
  const disabledStyle = disabled ? 'opacity-50 cursor-not-allowed' : ''

  // Clone icon and apply size styles
  const iconWithSize = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<Record<string, unknown>>, {
        className: `${iconSizeStyles[size]} ${(icon as React.ReactElement<Record<string, unknown>>).props?.className || ''}`,
      })
    : icon

  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`${baseStyles} ${variantStyle} ${sizeStyle} ${disabledStyle} ${className}`}
    >
      {iconWithSize}
    </button>
  )
}

export default IconButton
